import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms";
import {
  MoreOutlined,
  ShareAltOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Button } from "antd";
import { formatDistanceToNow } from "date-fns";
import { useMemo } from "react";
import CaptionExpand from "./components/CaptionExpand";
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface GoogleCardProps extends BaseSocialCardProps {
  title?: string;
  platformId: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

const GoogleCard = ({
  caption,
  mediaUrls = [],
  username = "Business Name",
  profileUrl,
  postedAt,
  platformId,
  ctaLabel,
  ctaUrl,
  isTemplateVideo,
}: GoogleCardProps) => {
  const captionLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  const displayCaption = useMemo(() => {
    if (captionLimit && caption.length > captionLimit) {
      return caption.substring(0, captionLimit) + "...";
    }
    return caption;
  }, [caption, captionLimit]);

  return (
    <div className="w-full bg-antd-colorBgContainer text-antd-colorText rounded-sm overflow-hidden shadow-md font-medium">
      <div className="px-4 py-3 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Avatar
            size={40}
            src={profileUrl}
            icon={!profileUrl ? <UserOutlined /> : undefined}
          />
          <div className="flex flex-col">
            <span className="font-semibold text-sm">{username}</span>
            <div className="flex items-center text-xs text-antd-colorTextSecondary">
              <span>
                {postedAt
                  ? formatDistanceToNow(new Date(postedAt), {
                      addSuffix: true,
                    })
                  : "Recently"}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-antd-colorTextSecondary">
            <MoreOutlined />
          </div>
        </div>
      </div>

      <div className="flex border-t border-b border-antd-colorBorderSecondary min-h-[200px]">
        <MediaSection mediaUrls={mediaUrls} isTemplateVideo={isTemplateVideo} />
      </div>

      {displayCaption && (
        <div className="px-4 pt-3 pb-1 font-normal">
          <CaptionExpand
            text={displayCaption}
            initialLines={4}
            showMoreText="... More"
            showLessText="...less"
            linkColor="gray"
            className="text-sm text-antd-colorText"
          />
        </div>
      )}

      <div className="px-4 py-3 flex justify-between items-center mt-1">
        <div>
          {ctaUrl && (
            <Button
              type="link"
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-0 h-auto"
            >
              {ctaLabel || "LEARN MORE"}
            </Button>
          )}
        </div>

        <div className="flex items-center gap-1 text-antd-colorTextSecondary text-sm cursor-pointer hover:text-antd-colorPrimary">
          <ShareAltOutlined style={{ fontSize: "20px" }} />
        </div>
      </div>
    </div>
  );
};

export default GoogleCard;
